// The top-right turn row (PLAN-UI §28.3, screen-mock `#turnrow`).
//
// Everything that answers "whose go is it, and what may I do about it": the
// state of the turn, how far the run has got, what is about to land, the room
// this game is in — then the three turn actions and the pause button.
//
// **It takes explicit props and reads no store.** The old header derived all
// of this inline and was therefore un-testable and un-movable; a zone that
// takes values is a zone GameShell can compose (PLAN-UI §28.5, U9).
//
// Registered with the inset registry on the `top` edge, so the camera knows
// what this covers (PLAN-UI §28.6).

import { type ReactNode, useState } from "react";

import type { useNet } from "src/client/ai/hooks/useNet";
import { beckoning, Btn, HoverCard, Icon, Pill, PillLabel, StatCounter, styled, useChromeRect } from "./kit";

/** How long "Copied" stays on the invite button. */
const COPIED_MS = 1600;

type Tone = "default" | "rust" | "rustFill" | "brand";

/**
 * A readout, tinted by what it is saying.
 *
 * Four tones and no more: ordinary, rust for something wrong, a rust fill for
 * the one thing that must be impossible to miss (a desync), and a brand edge
 * for something loud but not broken.
 */
const Chip = styled(Pill, {
  shouldForwardProp: (prop: string) => prop !== "tone"
})<{ tone?: Tone }>(({ theme, tone = "default" }) => {
  const c = theme.kit.color;
  if (tone === "rust") return { borderColor: c.rust, color: c.rust };
  if (tone === "rustFill")
    return { background: c.rust, borderColor: c.rustEdge, color: c.onRust };
  if (tone === "brand") return { borderColor: c.brand, color: c.brand };
  return {};
});

/** The room code is read off a screen and typed in, so it is display voice. */
const Code = styled(StatCounter)(({ theme }) => ({
  fontSize: 13,
  letterSpacing: 1,
  color: theme.kit.color.ink
}));

const Row = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 9
});

/** Who is in the room, one line each, on the roster chip's card. */
const Seats = styled("div")(({ theme }) => ({
  fontFamily: theme.kit.font.body,
  fontSize: 12,
  lineHeight: 1.5,
  color: theme.kit.color.inkDim
}));

/**
 * The moves counter, inside the turn indicator rather than beside it.
 *
 * Dimmer than the sentence it trails, because it is the same fact at a finer
 * grain — a second full-weight chip made the corner read as two readouts.
 */
const Moves = styled("span")(({ theme }) => ({
  marginLeft: 6,
  color: theme.kit.color.inkDim
}));

/** Square, because it holds a glyph and no word. */
const PauseBtn = styled(Btn)(({ theme }) => ({
  width: 40,
  height: 40,
  padding: 0,
  borderRadius: theme.kit.radius.card
}));

/**
 * The transport's own group, so it reads as one control and not five.
 *
 * Shared with the history transport (§29 U4), which is the same shape of
 * control in the same slot — a spectator drives the clock, a player steps
 * through what has already happened, and the two never draw together.
 */
export const Transport = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 6
});

/**
 * Off through `aria-disabled`, never the native attribute.
 *
 * `Btn`'s `:disabled` rule sets `pointer-events: none`, and a control that
 * cannot be hovered cannot say why it is off — which is the one job an off
 * control has (the `Tile` ruling from U4, and `HoverCard`'s own reason for
 * attaching pointer handlers rather than gating on the DOM).
 */
export const OffBtn = styled(Btn)({
  '&[aria-disabled="true"]': { opacity: 0.38, cursor: "default" }
});

/**
 * End turn, **named in words, not a stop glyph** — since §31 T2 a turn ends
 * only here, and an icon alone read as a media control. `ready` (moves spent,
 * clock still yours) is the one moment the game is waiting on this button,
 * so it breathes; reduced motion keeps the label and drops the pulse.
 */
const EndTurnBtn = styled(OffBtn, {
  shouldForwardProp: (prop: string) => prop !== "ready"
})<{ ready: boolean }>(({ theme, ready }) => ({
  fontWeight: 700,
  letterSpacing: "0.02em",
  whiteSpace: "nowrap",
  ...(ready
    ? {
        ...beckoning,
        outline: `2px solid ${theme.kit.color.brandHover}`,
        outlineOffset: 2
      }
    : {})
}));

export interface TurnRowProps {
  /** The game has ended — mate, stalemate or a draw. */
  terminal: boolean;
  /** What the status is called: "Check", "Checkmate", …, or "" while playing. */
  label: string;
  /**
   * "White to move", and the counter — "2/2" only when the turn holds more
   * than one move, which the engine decides (`turnLabel`). Never gated on the
   * seat: the label names the side, so "Black to move 2/2" is a true and
   * useful thing to read on the opponent's turn.
   */
  turn: { text: string; moves: string | null };
  /** Single player: which front, and the level actually being played. */
  stage: { number: number; level: string } | null;
  /** Turns before a camp lands on a front held open, or null when none is owed. */
  waveOwed: number | null;
  /** Whether the spawn clock ruleset is running — it changes the sentence. */
  spawnClock: boolean;
  /** The spawn clock's own countdown (§26 F10). Never both with `waveOwed`. */
  clockOwed: number | null;
  /** The room, as the net client sees it — the one fact here that is not the engine's. */
  net: ReturnType<typeof useNet>["net"];
  /**
   * Nobody is playing. The three actions go rather than grey out: a watched
   * game has no seat to issue them from, and that is fixed for the whole game
   * rather than a control flickering.
   */
  watching: boolean;
  /**
   * Why Undo is off, or null while it may act.
   *
   * **Disabled, never removed** (§29 U2). It used to be shown and hidden, on
   * the argument that the gate flipped once per run and a permanently dead
   * button says "you cannot do this" forever — and that argument went with the
   * gate. Undo is now something you may do on most turns, off on the ones
   * worth explaining, which is exactly End turn's shape beside it. The string
   * is the whole content of the off state, said on its hover card.
   */
  undoRefusal: string | null;
  onUndo(): void;
  /**
   * Why End turn is off, or null while it may act.
   *
   * Ending the turn forfeits every move left in it, costs nothing and is legal
   * for either side in any game — so unlike Undo it is **disabled, never
   * removed** (the persistent-chrome rule): it is a thing you may do on almost
   * every turn, and the two turns you may not are the ones worth explaining.
   * The string is the whole content of the off state, said on its hover card.
   */
  endTurnRefusal: string | null;
  /** Moves spent, clock still yours: the game is waiting on End turn. */
  endTurnReady?: boolean;
  onPass(): void;
  /**
   * The watch transport — the whole interface to a game nobody is playing.
   *
   * **It replaces the three turn actions rather than joining them** (§28.6:
   * watching is a whole seat), which is why it sits in their slot: they are
   * already hidden while watching, so nothing here ever draws beside them. A
   * speed control over a game somebody is *thinking* about would be a control
   * over their clock, which is why this is absent unless nobody holds a seat.
   *
   * Optional and defaulting to nothing, so a game with a player passes it not
   * at all.
   */
  transport?: {
    paused: boolean;
    onPause(paused: boolean): void;
    onStep(): void;
    pace: number;
    paces: readonly number[];
    onPace(rate: number): void;
  } | null;
  /**
   * The history transport (§29 U4), as a node.
   *
   * A node rather than a model because it is a *zone* — it reads the store for
   * the step it is on and owns the flash's timer — and this component takes
   * values and reads nothing. It sits beside the turn actions in the same
   * group the watch transport uses, and is absent while watching, where that
   * transport has the slot.
   */
  history?: ReactNode;
  onPause(): void;
  /** Straight back to the title screen; absent, the button is disabled. */
  onLeave?(): void;
}

export function TurnRow({
  terminal,
  label,
  turn,
  stage,
  waveOwed,
  spawnClock,
  clockOwed,
  net,
  watching,
  undoRefusal,
  onUndo,
  endTurnRefusal,
  endTurnReady,
  onPass,
  transport = null,
  history = null,
  onPause,
  onLeave
}: TurnRowProps) {
  const root = useChromeRect("turn-row", "top");
  const [copied, setCopied] = useState(false);

  return (
    <Row ref={root}>
      <Chip data-testid="turn-indicator" tone={terminal ? "rust" : "default"}>
        {terminal ? label : turn.text}
        {!terminal && turn.moves && <Moves data-testid="moves-left">{turn.moves}</Moves>}
      </Chip>

      {/* Check, which is not terminal and is still worth shouting. */}
      {!terminal && label && <Chip tone="rust">{label}</Chip>}

      {/* What is being *played*, not what was picked: a run works up to its
          difficulty, so a chip reading "Hard" over an opponent deliberately
          playing easy is the label lying about the game. */}
      {stage && (
        <HoverCard
          placement="bottom"
          title="The front"
          card="Checkmate the opponent and a fresh force takes the field."
        >
          <Chip data-testid="stage-indicator">
            {`Stage ${stage.number} · ${stage.level}`}
          </Chip>
        </HoverCard>
      )}

      {/*
        **A front held open says so, or the breather reads as the game having
        stopped.** The board goes quiet at exactly the moment a player expects
        the next camp — no enemy, nothing to fight, the picker gone — and
        without a number on screen the only honest reading of that is a bug.

        It counts the same subtraction the reducer's gate is written on
        (`waveIn`), which is the despair banner's rule: a countdown that
        disagrees with what it is counting down to is worse than none.
      */}
      {waveOwed !== null && (
        <HoverCard
          placement="bottom"
          title="The next camp"
          card={
            // Under the spawn clock an owed camp is usually a front closed with
            // another enemy still fighting (`spawnHoldsNext`), so "the turns are
            // yours" would be a lie — the clock's sentence is the true one there.
            spawnClock
              ? "The spawn clock is running. The next camp lands when this runs out, whether or not the last one is beaten."
              : "The front is clear. The next camp lands when this runs out — the turns until then are yours."
          }
        >
          <Chip data-testid="wave-countdown" tone="brand">
            <PillLabel>Wave</PillLabel>
            {waveOwed === 0 ? "landing" : `in ${waveOwed}`}
          </Chip>
        </HoverCard>
      )}

      {/*
        **The spawn clock counts down on screen too** (§26 F10), through the
        same chip in the same slot — the sentence differs because the moment
        does: this camp lands on a fight already going, beaten or not, and a
        camp that simply appears reads as the game cheating.
      */}
      {clockOwed !== null && (
        <HoverCard
          placement="bottom"
          title="The spawn clock"
          card="The spawn clock is running. The next camp lands when this runs out, whether or not the last one is beaten."
        >
          <Chip data-testid="wave-countdown" tone="brand">
            <PillLabel>Wave</PillLabel>
            {clockOwed === 0 ? "landing" : `in ${clockOwed}`}
          </Chip>
        </HoverCard>
      )}

      {/*
        The code, not just the link. A link is the easy path and is what the
        address bar already is — but two people in the same room read a code off
        a screen, and twenty hex characters could not be read off anything.
      */}
      {net.status === "online" && net.room && (
        <HoverCard
          placement="bottom"
          title="Room code"
          card="Whoever has it can join this game."
        >
          <Chip data-testid="room-code">
            <Code>{net.room}</Code>
          </Chip>
        </HoverCard>
      )}

      {/* The chip is the names; the card is who is sitting where. A `title`
          could only join them with newlines — which a browser tooltip renders
          and nothing else does. */}
      {net.status === "online" && (net.occupants?.length ?? 0) > 0 && (
        <HoverCard
          placement="bottom"
          title="In this room"
          card={
            <Seats>
              {net.occupants!.map((one) => (
                <div key={`${one.name}:${one.seat}`}>{`${one.name} — ${one.seat}`}</div>
              ))}
            </Seats>
          }
        >
          <Chip data-testid="roster">{net.occupants!.map((one) => one.name).join(" · ")}</Chip>
        </HoverCard>
      )}

      {net.status === "online" && net.room && (
        <Btn
          small
          data-testid="share-link"
          variant={copied ? "cream" : "default"}
          onClick={() => {
            const url = new URL(window.location.href);
            url.searchParams.set("room", net.room as string);
            void navigator.clipboard?.writeText(url.toString());
            setCopied(true);
            window.setTimeout(() => setCopied(false), COPIED_MS);
          }}
        >
          Copy invite link
        </Btn>
      )}

      {net.status === "failed" && (
        <Chip data-testid="net-failed" tone="rust">
          {`Could not join: ${net.reason ?? "refused"}`}
        </Chip>
      )}

      {net.status !== "offline" && net.status !== "failed" && (
        <Chip
          data-testid="net-status"
          // A desync is a determinism bug, not an attack (PLAN §5.5), and it
          // must be impossible to miss — a client quietly drifting from the
          // server is the one failure that corrupts a game silently. **A stale
          // tab is loud but not red**: it is the ordinary state of every open
          // page for a few minutes after a deploy, and the answer is a reload
          // rather than a bug report.
          tone={
            net.status === "desynced" ? "rustFill" : net.status === "stale" ? "brand" : "default"
          }
        >
          {net.status === "stale"
            ? "Out of date — reload"
            : net.status === "desynced"
              ? `DESYNC @${net.desync?.seq}`
              : `${net.status}${net.seat ? ` · ${net.seat}` : ""}`}
        </Chip>
      )}

      {/*
        Undo, in End turn's shape and for End turn's reason: it is a thing you
        may do on most turns, so its absence would say more than its off state
        does, and the turns it cannot act on are the ones worth a sentence.
      */}
      {!watching && (
        <HoverCard
          placement="bottom"
          title="Undo"
          card={undoRefusal ?? "Take back the last thing you did this turn. Settings stay."}
        >
          <OffBtn
            icon
            data-testid="undo"
            aria-disabled={undoRefusal !== null}
            onClick={() => {
              if (undoRefusal === null) onUndo();
            }}
          >
            {/* The circular arrow, and it is the button's only name now — no
                `aria-label` beside it, or the control announces itself twice. */}
            <Icon name="undo" label="Undo" />
          </OffBtn>
        </HoverCard>
      )}
      {/*
        End turn stays on screen whether or not it may act — the one turn
        action that is nearly always available, so its absence would say more
        than its off state does. `aria-disabled` rather than the native
        attribute, or the card explaining the two exceptions could not be
        hovered.
      */}
      {!watching && (
        <HoverCard
          placement="bottom"
          title="End turn"
          card={
            endTurnRefusal ??
            (endTurnReady
              ? "Your moves are spent. Build, re-point or undo first if you like — then hand the turn on."
              : "Give up every move left in this turn. The factories still run.")
          }
        >
          <EndTurnBtn
            data-testid="pass-turn"
            variant="primary"
            ready={endTurnReady === true}
            aria-disabled={endTurnRefusal !== null}
            onClick={() => {
              if (endTurnRefusal === null) onPass();
            }}
          >
            <Icon name="stop" />
            End turn
          </EndTurnBtn>
        </HoverCard>
      )}
      {!watching && history}

      {/*
        The transport, in the slot the three turn actions vacate.

        **Step is off rather than gone while it is running**, which is the
        opposite call to the one the three actions take, and the difference is
        how often the state flips: pause-then-step is one gesture, and a
        button that materialised under the pointer halfway through it would be
        caught by the same click. So it stays, greyed, and its card says what
        to do first.
      */}
      {transport && (
        <Transport data-testid="transport">
          <HoverCard
            placement="bottom"
            title="Transport"
            card={
              transport.paused ? "Let the machines play on" : "Stop after the turn in flight"
            }
          >
            <Btn
              small
              data-testid="watch-pause"
              aria-pressed={transport.paused}
              variant={transport.paused ? "cream" : "default"}
              onClick={() => transport.onPause(!transport.paused)}
            >
              {transport.paused ? "Run" : "Pause"}
            </Btn>
          </HoverCard>

          <HoverCard
            placement="bottom"
            title="Step"
            card={
              transport.paused
                ? "Play exactly one more turn"
                : "Pause first to step a turn at a time"
            }
          >
            <OffBtn
              small
              data-testid="watch-step"
              aria-disabled={!transport.paused}
              onClick={() => {
                if (transport.paused) transport.onStep();
              }}
            >
              Step
            </OffBtn>
          </HoverCard>

          {transport.paces.map((rate) => (
            <HoverCard
              key={rate}
              placement="bottom"
              title="Pace"
              card={`Play at ${rate} times the ordinary speed`}
            >
              <Btn
                small
                data-testid={`pace-${rate}`}
                aria-pressed={transport.pace === rate}
                variant={transport.pace === rate ? "cream" : "default"}
                onClick={() => transport.onPace(rate)}
              >
                {rate}×
              </Btn>
            </HoverCard>
          ))}
        </Transport>
      )}

      <HoverCard placement="bottom-end" title="Leave" card="Back to the title screen. A run keeps its save.">
        <PauseBtn data-testid="leave-to-title" disabled={!onLeave} onClick={() => onLeave?.()}>
          <Icon name="close" label="Leave" />
        </PauseBtn>
      </HoverCard>

      <HoverCard placement="bottom-end" title="Menu" card="Settings, rules and the way out. Esc.">
        {/* No `aria-label` here: the icon's own `label` is already this
            button's accessible name, and two would announce it twice. */}
        <PauseBtn data-testid="pause-menu" onClick={onPause}>
          <Icon name="pause" label="Menu" />
        </PauseBtn>
      </HoverCard>
    </Row>
  );
}
